// Loads the real account state the workflow rail reasons about.
//
// Real mode reads Convex reactively, so the rail updates the moment a
// simulation finishes or a juror is generated, with no refetch. Demo mode
// falls back to the same canned Express clients every other demo page uses.
//
// Deliberately does NOT use useSelectedCase: that hook writes `?case=<id>`
// into the URL via history.replaceState on mount, and the rail renders on
// every authenticated page. Reading the same three sources without writing
// keeps the rail a passive observer.

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { useAuth } from '@/hooks/useAuth';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import {
  casesApi,
  simulationsApi,
  personasApi,
  tasksApi,
  deadlinesApi,
  type CaseTask,
} from '@/services/api';
import type { Case, CaseDeadline, SimulationSession } from '@/types';
import type {
  SnapshotCase,
  SnapshotDeadline,
  SnapshotSimulation,
  SnapshotTask,
  WorkflowSnapshot,
} from '@/lib/recommendedSteps';

/** Same key useSelectedCase persists to, so the rail agrees with the pages. */
const SELECTED_CASE_KEY = 'selectedCaseId';

/**
 * The only four case fields the rail needs. Kept narrow on purpose so the
 * Convex documents and the snake_case demo `Case` can share one shape without
 * either side having to be fully adapted.
 */
interface RailCase {
  id: string;
  name: string;
  status: string;
  caseTheory: string | null;
}

function readStoredCaseId(): string | null {
  try {
    return localStorage.getItem(SELECTED_CASE_KEY);
  } catch {
    return null;
  }
}

function toSnapshotCase(c: RailCase): SnapshotCase {
  return {
    id: c.id,
    name: c.name,
    status: c.status,
    hasTheory: Boolean(c.caseTheory && c.caseTheory.trim().length > 0),
  };
}

export function useWorkflowSnapshot(): WorkflowSnapshot {
  const { isDemoMode, isAuthenticated } = useAuth();
  const location = useLocation();

  const urlCaseId = useMemo(
    () => new URLSearchParams(location.search).get('case'),
    [location.search]
  );

  // ─── Real mode: reactive Convex ────────────────────────────────────────────
  const skipReal = isDemoMode || !isAuthenticated;
  const convexCases = useQuery(api.cases.list, skipReal ? 'skip' : {});
  const convexTasks = useQuery(api.tasks.listAllForUser, skipReal ? 'skip' : {});
  const convexDeadlines = useQuery(api.deadlines.listAllForUser, skipReal ? 'skip' : {});

  const realCases = useMemo<RailCase[] | undefined>(
    () =>
      convexCases?.map((doc: any) => ({
        id: doc._id,
        name: doc.name,
        status: doc.status ?? 'active',
        caseTheory: doc.caseTheory ?? null,
      })),
    [convexCases]
  );

  // ─── Demo mode: one-shot canned data ───────────────────────────────────────
  const [demoCases, setDemoCases] = useState<RailCase[] | undefined>(undefined);
  const [demoTasks, setDemoTasks] = useState<CaseTask[]>([]);
  const [demoDeadlines, setDemoDeadlines] = useState<CaseDeadline[]>([]);

  useEffect(() => {
    if (!isDemoMode || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      const [cases, tasks, deadlines] = await Promise.all([
        casesApi.getAll().then((r: { cases?: Case[] }) => r.cases ?? []).catch(() => [] as Case[]),
        tasksApi.getAll().then((r) => r.tasks ?? []).catch(() => [] as CaseTask[]),
        deadlinesApi.getAll().then((r) => r.deadlines ?? []).catch(() => [] as CaseDeadline[]),
      ]);
      if (cancelled) return;
      setDemoCases(
        cases.map((c) => ({
          id: c.id,
          name: c.name,
          status: c.status,
          caseTheory: c.case_theory ?? null,
        }))
      );
      setDemoTasks(tasks);
      setDemoDeadlines(deadlines);
    })();
    return () => {
      cancelled = true;
    };
  }, [isDemoMode, isAuthenticated]);

  const cases = isDemoMode ? demoCases : realCases;
  const casesLoading = cases === undefined;

  // ─── Resolve the active case: URL, then storage, then newest ───────────────
  const activeCase = useMemo<RailCase | undefined>(() => {
    if (!cases || cases.length === 0) return undefined;
    const fromUrl = urlCaseId ? cases.find((c) => c.id === urlCaseId) : undefined;
    if (fromUrl) return fromUrl;
    const storedId = readStoredCaseId();
    const fromStorage = storedId ? cases.find((c) => c.id === storedId) : undefined;
    if (fromStorage) return fromStorage;
    // cases.list already sorts newest first.
    return cases[0];
  }, [cases, urlCaseId]);

  const activeCaseId = activeCase?.id;

  // ─── Per-case detail for the active case only ──────────────────────────────
  const skipCaseScoped = skipReal || !activeCaseId;
  const convexSims = useQuery(
    api.simulations.listByCase,
    skipCaseScoped ? 'skip' : { caseId: activeCaseId as Id<'cases'> }
  );
  const convexJurors = useQuery(
    api.personas.listByCase,
    skipCaseScoped ? 'skip' : { caseId: activeCaseId as Id<'cases'> }
  );

  const [demoSims, setDemoSims] = useState<SimulationSession[] | undefined>(undefined);
  const [demoJurorCount, setDemoJurorCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!isDemoMode || !activeCaseId) return;
    let cancelled = false;
    (async () => {
      const [sims, jurors] = await Promise.all([
        simulationsApi.getByCaseId(activeCaseId).then((r) => r.sessions ?? []).catch(() => []),
        personasApi.getCustom(activeCaseId).then((r) => r.personas ?? []).catch(() => []),
      ]);
      if (cancelled) return;
      setDemoSims(sims);
      setDemoJurorCount(jurors.length);
    })();
    return () => {
      cancelled = true;
    };
  }, [isDemoMode, activeCaseId]);

  return useMemo<WorkflowSnapshot>(() => {
    const simulations: SnapshotSimulation[] = isDemoMode
      ? (demoSims ?? []).map((s) => ({
          id: s.id,
          status: s.status,
          createdAt: Date.parse(s.created_at ?? s.started_at ?? '') || 0,
        }))
      : (convexSims ?? []).map((s: any) => ({
          id: s._id,
          status: s.status,
          createdAt: s._creationTime,
        }));

    const jurorCount = isDemoMode
      ? demoJurorCount
      : convexJurors === undefined
        ? undefined
        : convexJurors.length;

    const rawTasks = isDemoMode ? demoTasks : (convexTasks ?? []);
    const tasks: SnapshotTask[] = rawTasks.map((t: any) => ({
      title: t.title,
      status: t.status,
      dueDate: t.due_date ?? t.dueDate ?? '',
    }));

    const rawDeadlines = isDemoMode ? demoDeadlines : (convexDeadlines ?? []);
    const deadlines: SnapshotDeadline[] = rawDeadlines
      .map((d: any) => ({ title: d.title, date: d.date, status: d.status }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      // Case-scoped queries are allowed to still be in flight: the rail shows
      // what it knows and fills in the rest reactively. Only the case list
      // gates the whole rail, because every rule depends on it.
      loading: casesLoading,
      caseCount: cases?.length ?? 0,
      activeCase: activeCase ? toSnapshotCase(activeCase) : undefined,
      jurorCount,
      simulations,
      deadlines,
      tasks,
    };
  }, [
    isDemoMode,
    casesLoading,
    cases,
    activeCase,
    convexSims,
    convexJurors,
    convexTasks,
    convexDeadlines,
    demoSims,
    demoJurorCount,
    demoTasks,
    demoDeadlines,
  ]);
}
